import React, { useState } from 'react'
import { Stack, IconButton, Grid, Typography } from '@mui/material'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { J2Add } from '@e3dc-react/icons'
import TimeFrame from './TimeFrame'
import { useApp } from 'src/Hooks'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ConfirmationDialog } from '@e3dc-react/shell/Elements/Dialog'
import { TimeFrameModel } from 'src/Libs/dynamic-tariff-settings/model'
import { beginningOfDay, endOfDay, VariableGridFeesTranslationKey } from 'src/Libs/dynamic-tariff-settings/constants'

interface Props {
  dateFrameName: string
  hasWritingRights: boolean
}

/**
 * Component to add TimeFrame to react-hook-form field array
 */
const TimeFrameList: React.FC<Props> = ({ dateFrameName, hasWritingRights }: Props) => {
  const { control, getValues } = useFormContext()
  const { fields, append, remove, update } = useFieldArray({
    name: `${dateFrameName}.timeFrames`,
    control,
  })

  const { t } = useCustomTranslation()
  const { isMobile: isDownSm } = useApp()
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [timeFrameIndex, setTimeFrameIndex] = useState<number>(0)

  /**
   * add time frame to the list.
   * before adding, ensures that first 'from' starts at 00:00 and last 'to' ends at 23:59
   * example: [{from: 00:00, to: 12:00}, {from: 12:00, to: 23:59}]
   */
  const handleAddTimeFrame = (): void => {
    const timeFrames = getValues(`${dateFrameName}.timeFrames`) as TimeFrameModel[]
    const hasTimeFrames = timeFrames.length > 0

    /* before adding a new time frame, reset 'to' field of last time frame from '23:59' to '00:00' */
    if (hasTimeFrames) {
      const lastTimeFrameIndex = timeFrames.length - 1
      const lastTimeFrame = timeFrames[lastTimeFrameIndex]
      update(lastTimeFrameIndex, { ...lastTimeFrame, to: beginningOfDay })
    }

    /* add new time frame with default "from" and 'to' time '00:00' and '23:59' of the same day */
    append({
      from: beginningOfDay,
      to: endOfDay,
      price: 0,
      description: '',
    })
  }

  const handleOpenConfirmationDialog = (index: number): void => {
    setTimeFrameIndex(index)
    setConfirmationOpen(true)
  }

  /**
   * removes the selected time frame from the list.
   * before removing, ensures that first 'from' starts at 00:00 and last 'to' ends at 23:59
   * example: [{from: 00:00, to: 12:00}, {from: 12:00, to: 23:59}]
   */
  const handleRemoveTimeFrame = (): void => {
    const timeFrames = getValues(`${dateFrameName}.timeFrames`) as TimeFrameModel[]
    const isDeletingFirstTimeFrame = timeFrameIndex === 0
    const isDeletingLastTimeFrame = timeFrameIndex === timeFrames.length - 1
    const hasMultipleTimeFrames = timeFrames.length > 1

    /* after deleting first time frame, 2nd time frame's 'from' field should now start at 00:00 */
    if (hasMultipleTimeFrames && isDeletingFirstTimeFrame) {
      const nextTimeFrameIndex = timeFrameIndex + 1
      const nextTimeFrame = timeFrames[nextTimeFrameIndex]
      update(nextTimeFrameIndex, { ...nextTimeFrame, from: beginningOfDay })
    }

    /* after deleting last time frame, 2nd last time frame's 'to' field should now end at 23:59 */
    if (hasMultipleTimeFrames && isDeletingLastTimeFrame) {
      const previousTimeFrameIndex = timeFrameIndex - 1
      const previousTimeFrame = timeFrames[previousTimeFrameIndex]
      update(previousTimeFrameIndex, { ...previousTimeFrame, to: endOfDay })
    }

    remove(timeFrameIndex)
    setConfirmationOpen(false)
  }

  const timeFrameHeaders = (
    <Grid container>
      <Grid item xs={2} md={2}>
        <Typography variant="body1">{t(VariableGridFeesTranslationKey.From)}</Typography>
      </Grid>
      <Grid item xs={2} md={2}>
        <Typography variant="body1">{t(VariableGridFeesTranslationKey.Until)}</Typography>
      </Grid>
      <Grid item xs={2} md={2}>
        <Typography variant="body1">{t(VariableGridFeesTranslationKey.Fees)}</Typography>
      </Grid>
      <Grid item xs={6} md={6}>
        <Typography variant="body1">{t(VariableGridFeesTranslationKey.Description)}</Typography>
      </Grid>
    </Grid>
  )

  return (
    <>
      {confirmationOpen && (
        <ConfirmationDialog
          onClose={() => setConfirmationOpen(false)}
          onCancel={() => setConfirmationOpen(false)}
          onConfirm={() => {
            handleRemoveTimeFrame()
          }}
          text={t('dialog.confirmDelete.text')}
          title={t('dialog.confirmDelete.title')}
          open={confirmationOpen}
        />
      )}
      <Stack gap={2}>
        {!isDownSm && timeFrameHeaders}

        {fields.map((field, index) => {
          return (
            <TimeFrame
              hasWritingRights={hasWritingRights}
              key={field.id}
              dateFrameName={dateFrameName}
              timeFrameIndex={index}
              handleOpenConfirmationDialog={handleOpenConfirmationDialog}
            />
          )
        })}

        <IconButton disabled={!hasWritingRights} sx={{ m: -1, color: 'grey.grey00' }} onClick={handleAddTimeFrame}>
          <J2Add />
        </IconButton>
      </Stack>
    </>
  )
}

export default TimeFrameList
